import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { v4 as uuidv4 } from 'uuid';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for managing lecture CRUD operations with GraphQL API
 */
export const useLectureAPI = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch an existing lecture by ID
   */
  const fetchLecture = useCallback(async (lectureId) => {
    if (!lectureId) return null;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
          query GetLecture($id: ID!) {
            getLecture(id: $id) {
              id
              type
              event {
                id
                name {
                  language
                  text
                }
                status
                date
                lectureDocumentsEndDate
              }
              company {
                id
                name
                industry
                status
              }
              speakers {
                items {
                  employeeId
                  employee {
                    id
                    user {
                      firstName
                      lastName
                      middleName
                      title
                    }
                    image {
                      fileName
                      alt
                    }
                  }
                }
              }
              speakersUsers {
                items {
                  userId
                  user {
                    id
                    firstName
                    lastName
                    middleName
                    title
                    email
                    image {
                      fileName
                      alt
                    }
                  }
                }
              }
              mainSpeaker {
                id
                user {
                  firstName
                  lastName
                  middleName
                  title
                }
                image {
                  fileName
                  alt
                }
              }
              mainSpeakerUser {
                id
                firstName
                lastName
                middleName
                title
                email
                image {
                  fileName
                  alt
                }
              }
              title {
                language
                text
              }
              subjects {
                id
                name {
                  language
                  text
                }
              }
              topics {
                items {
                  topicId
                  topic {
                    id
                    name {
                      language
                      text
                    }
                  }
                }
              }
              content {
                language
                text
              }
              conditions {
                name
                accepted
              }
              presentationLanguage
              image {
                fileName
                alt
              }
              pdfDe {
                url
                name
                s3Path
                fileName
              }
              pdfEn {
                url
                name
                s3Path
                fileName
              }
              presentation {
                url
                name
                s3Path
                fileName
              }
              companyBooth
              createdAt
              updatedAt
              companyLecturesId
              eventLecturesId
              industryLecturesId
              lectureMainSpeakerId
              lectureMainSpeakerUserId
              speakerAffiliations {
                speakerId
                source
                companyId
                companyName
              }
            }
          }
        `,
        variables: { id: lectureId }
      });
      
      return response.data.getLecture;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'fetch-lecture',
        consoleMessage: 'Error fetching lecture:',
        context: { lectureId }
      });
      setError(error.message || 'Failed to fetch lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Create a new lecture
   */
  const createLecture = useCallback(async (lectureData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Create the main lecture record
      const lectureResponse = await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecture(
            $input: CreateLectureInput!
            $condition: ModelLectureConditionInput
          ) {
            createLecture(input: $input, condition: $condition) {
              id
              type
              presentationLanguage
              title {
                language
                text
              }
              subjects {
                id
                name {
                  language
                  text
                }
              }
              content {
                language
                text
              }
              conditions {
                name
                accepted
              }
              image {
                fileName
                alt
              }
              pdfDe {
                fileName
                name
                s3Path
              }
              pdfEn {
                fileName
                name
                s3Path
              }
              presentation {
                fileName
                name
                s3Path
              }
              companyBooth
              companyLecturesId
              eventLecturesId
              industryLecturesId
              lectureMainSpeakerId
            }
          }
        `,
        variables: {
          input: {
            id: uuidv4(),
            type: lectureData.type,
            presentationLanguage: lectureData.presentationLanguage,
            title: lectureData.title,
            subjects: lectureData.subjects,
            content: lectureData.content,
            conditions: lectureData.conditions,
            image: lectureData.image,
            pdfDe: lectureData.pdfDe,
            pdfEn: lectureData.pdfEn,
            presentation: lectureData.presentation,
            companyBooth: lectureData.companyBooth,
            companyLecturesId: lectureData.companyId,
            eventLecturesId: lectureData.eventId,
            industryLecturesId: lectureData.industryLecturesId,
            lectureMainSpeakerId: lectureData.mainSpeakerId,
            lectureMainSpeakerUserId: lectureData.mainSpeakerUserId,
            ...(lectureData.speakerAffiliations?.length ? { speakerAffiliations: lectureData.speakerAffiliations } : {})
          }
        }
      });

      const lecture = lectureResponse.data.createLecture;

      // Create speaker relations (employees)
      if (lectureData.speakers && lectureData.speakers.length > 0) {
        await Promise.all(
          lectureData.speakers.map(speakerId =>
            createLectureSpeakerRelation(lecture.id, speakerId)
          )
        );
      }

      // Create speaker user relations
      if (lectureData.speakerUsers && lectureData.speakerUsers.length > 0) {
        await Promise.all(
          lectureData.speakerUsers.map(userId =>
            createLectureSpeakerUserRelation(lecture.id, userId)
          )
        );
      }

      // Create topic relations
      if (lectureData.topics && lectureData.topics.length > 0) {
        await Promise.all(
          lectureData.topics.map(topicId =>
            createLectureTopicRelation(lecture.id, topicId)
          )
        );
      }

      // Fetch the complete lecture with all relations after creating
      const completeLecture = await fetchLecture(lecture.id);
      return completeLecture || lecture;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'create-lecture',
        consoleMessage: 'Error creating lecture:',
        context: {
          lectureData: {
            type: lectureData.type,
            companyId: lectureData.companyId,
            eventId: lectureData.eventId,
            industryLecturesId: lectureData.industryLecturesId,
            title: lectureData.title,
            presentationLanguage: lectureData.presentationLanguage,
            mainSpeakerId: lectureData.mainSpeakerId,
            mainSpeakerUserId: lectureData.mainSpeakerUserId,
            image: lectureData.image,
            pdfDe: lectureData.pdfDe,
            pdfEn: lectureData.pdfEn,
            presentation: lectureData.presentation,
            speakersCount: lectureData.speakers?.length || 0,
            speakerUsersCount: lectureData.speakerUsers?.length || 0,
            topicsCount: lectureData.topics?.length || 0
          }
        }
      });
      setError(error.message || 'Failed to create lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update an existing lecture
   */
  const updateLecture = useCallback(async (lectureId, lectureData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Update the main lecture record
      const lectureResponse = await client.graphql({
        query: /* GraphQL */ `
          mutation UpdateLecture(
            $input: UpdateLectureInput!
            $condition: ModelLectureConditionInput
          ) {
            updateLecture(input: $input, condition: $condition) {
              id
              type
              presentationLanguage
              title {
                language
                text
              }
              subjects {
                id
                name {
                  language
                  text
                }
              }
              content {
                language
                text
              }
              conditions {
                name
                accepted
              }
              image {
                fileName
                alt
              }
              pdfDe {
                fileName
                name
                s3Path
              }
              pdfEn {
                fileName
                name
                s3Path
              }
              presentation {
                fileName
                name
                s3Path
              }
              companyBooth
              speakers {
                items {
                  employee {
                    id
                    user {
                      firstName
                      lastName
                      middleName
                      title
                    }
                    image {
                      fileName
                      alt
                    }
                  }
                }
              }
              speakersUsers {
                items {
                  userId
                  user {
                    id
                    firstName
                    lastName
                    middleName
                    title
                    email
                    image {
                      fileName
                      alt
                    }
                  }
                }
              }
              mainSpeaker {
                id
                user {
                  firstName
                  lastName
                  middleName
                  title
                }
                image {
                  fileName
                  alt
                }
              }
              mainSpeakerUser {
                id
                firstName
                lastName
                middleName
                title
                email
                image {
                  fileName
                  alt
                }
              }
              topics {
                items {
                  topicId
                  topic {
                    id
                    name {
                      language
                      text
                    }
                  }
                }
              }
              companyLecturesId
              eventLecturesId
              industryLecturesId
              lectureMainSpeakerId
              lectureMainSpeakerUserId
              createdAt
              updatedAt
              startTime
              endTime
              room
              session
              adminComment
            }
          }
        `,
        variables: {
          input: {
            id: lectureId,
            type: lectureData.type,
            presentationLanguage: lectureData.presentationLanguage,
            title: lectureData.title,
            subjects: lectureData.subjects,
            content: lectureData.content,
            conditions: lectureData.conditions,
            image: lectureData.image,
            // Explicitly set to null if undefined to ensure deletion
            pdfDe: lectureData.pdfDe !== undefined ? lectureData.pdfDe : null,
            pdfEn: lectureData.pdfEn !== undefined ? lectureData.pdfEn : null,
            presentation: lectureData.presentation !== undefined ? lectureData.presentation : null,
            companyBooth: lectureData.companyBooth !== undefined ? lectureData.companyBooth : null,
            lectureMainSpeakerId: lectureData.mainSpeakerId,
            lectureMainSpeakerUserId: lectureData.mainSpeakerUserId,
            // Always send the list so removed entries are cleared as well
            speakerAffiliations: lectureData.speakerAffiliations || []
          }
        }
      });

      const lecture = lectureResponse.data.updateLecture;

      // Update speaker relations (employees)
      await updateLectureSpeakers(lectureId, lectureData.speakers || []);

      // Update speaker user relations
      await updateLectureSpeakerUsers(lectureId, lectureData.speakerUsers || []);

      // Update topic relations
      await updateLectureTopics(lectureId, lectureData.topics || []);

      // Fetch the complete lecture with all updated relations
      const completeLecture = await fetchLecture(lectureId);
      return completeLecture || lecture;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'update-lecture',
        consoleMessage: 'Error updating lecture:',
        context: {
          lectureId,
          lectureData: {
            type: lectureData.type,
            companyId: lectureData.companyId,
            eventId: lectureData.eventId,
            title: lectureData.title,
            presentationLanguage: lectureData.presentationLanguage,
            mainSpeakerId: lectureData.mainSpeakerId,
            mainSpeakerUserId: lectureData.mainSpeakerUserId,
            image: lectureData.image,
            pdfDe: lectureData.pdfDe,
            pdfEn: lectureData.pdfEn,
            presentation: lectureData.presentation,
            speakersCount: lectureData.speakers?.length || 0,
            speakerUsersCount: lectureData.speakerUsers?.length || 0,
            topicsCount: lectureData.topics?.length || 0
          }
        }
      });
      setError(error.message || 'Failed to update lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Delete a lecture
   */
  const deleteLecture = useCallback(async (lectureId) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // First delete all speaker and topic relations
      await deleteAllLectureSpeakers(lectureId);
      await deleteAllLectureTopics(lectureId);

      // Then delete the lecture itself
      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation DeleteLecture(
            $input: DeleteLectureInput!
            $condition: ModelLectureConditionInput
          ) {
            deleteLecture(input: $input, condition: $condition) {
              id
            }
          }
        `,
        variables: {
          input: { id: lectureId }
        }
      });

      return response.data.deleteLecture;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'delete-lecture',
        consoleMessage: 'Error deleting lecture:',
        context: {
          lectureId,
          attemptedOperation: 'delete'
        }
      });
      setError(error.message || 'Failed to delete lecture');
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Helper functions for managing many-to-many relations

  const createLectureSpeakerRelation = async (lectureId, employeeId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecturesSpeakers(
            $input: CreateLecturesSpeakersInput!
            $condition: ModelLecturesSpeakersConditionInput
          ) {
            createLecturesSpeakers(input: $input, condition: $condition) {
              id
              lectureId
              employeeId
            }
          }
        `,
        variables: {
          input: {
            lectureId,
            employeeId
          }
        }
      });
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'create-speaker-relation',
        consoleMessage: 'Error creating speaker relation:',
        context: { lectureId, employeeId }
      });
    }
  };

  const createLectureSpeakerUserRelation = async (lectureId, userId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecturesSpeakerUsers(
            $input: CreateLecturesSpeakerUsersInput!
            $condition: ModelLecturesSpeakerUsersConditionInput
          ) {
            createLecturesSpeakerUsers(input: $input, condition: $condition) {
              id
              lectureId
              userId
            }
          }
        `,
        variables: {
          input: {
            lectureId,
            userId
          }
        }
      });
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'create-speaker-user-relation',
        consoleMessage: 'Error creating speaker user relation:',
        context: { lectureId, userId }
      });
    }
  };

  const createLectureTopicRelation = async (lectureId, topicId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
          mutation CreateLecturesTopics(
            $input: CreateLecturesTopicsInput!
            $condition: ModelLecturesTopicsConditionInput
          ) {
            createLecturesTopics(input: $input, condition: $condition) {
              id
              lectureId
              topicId
            }
          }
        `,
        variables: {
          input: {
            lectureId,
            topicId
          }
        }
      });
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'create-topic-relation',
        consoleMessage: 'Error creating topic relation:',
        context: { lectureId, topicId }
      });
    }
  };

  const updateLectureSpeakers = async (lectureId, newSpeakerIds) => {
    // Fetch existing speakers
    const existingResponse = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesSpeakers(
          $filter: ModelLecturesSpeakersFilterInput
          $limit: Int
        ) {
          listLecturesSpeakers(filter: $filter, limit: $limit) {
            items {
              id
              employeeId
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const existingSpeakers = existingResponse.data.listLecturesSpeakers.items || [];
    const existingSpeakerIds = existingSpeakers.map(s => s.employeeId);

    // Determine speakers to add and remove
    const speakersToAdd = newSpeakerIds.filter(id => !existingSpeakerIds.includes(id));
    const speakersToRemove = existingSpeakers.filter(s => !newSpeakerIds.includes(s.employeeId));

    // Add new speakers
    await Promise.all(
      speakersToAdd.map(speakerId => createLectureSpeakerRelation(lectureId, speakerId))
    );

    // Remove obsolete speakers
    await Promise.all(
      speakersToRemove.map(speaker =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesSpeakers(
              $input: DeleteLecturesSpeakersInput!
              $condition: ModelLecturesSpeakersConditionInput
            ) {
              deleteLecturesSpeakers(input: $input, condition: $condition) {
                id
              }
            }
          `,
          variables: {
            input: { id: speaker.id }
          }
        })
      )
    );
  };

  const updateLectureSpeakerUsers = async (lectureId, newUserIds) => {
    // Fetch existing speaker users
    const existingResponse = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesSpeakerUsers(
          $filter: ModelLecturesSpeakerUsersFilterInput
          $limit: Int
        ) {
          listLecturesSpeakerUsers(filter: $filter, limit: $limit) {
            items {
              id
              userId
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const existingSpeakerUsers = existingResponse.data.listLecturesSpeakerUsers.items || [];
    const existingUserIds = existingSpeakerUsers.map(s => s.userId);

    // Determine users to add and remove
    const usersToAdd = newUserIds.filter(id => !existingUserIds.includes(id));
    const usersToRemove = existingSpeakerUsers.filter(s => !newUserIds.includes(s.userId));

    // Add new speaker users
    await Promise.all(
      usersToAdd.map(userId => createLectureSpeakerUserRelation(lectureId, userId))
    );

    // Remove obsolete speaker users
    await Promise.all(
      usersToRemove.map(speakerUser =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesSpeakerUsers(
              $input: DeleteLecturesSpeakerUsersInput!
              $condition: ModelLecturesSpeakerUsersConditionInput
            ) {
              deleteLecturesSpeakerUsers(input: $input, condition: $condition) {
                id
              }
            }
          `,
          variables: {
            input: { id: speakerUser.id }
          }
        })
      )
    );
  };

  const updateLectureTopics = async (lectureId, newTopicIds) => {
    // Fetch existing topics
    const existingResponse = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesTopics(
          $filter: ModelLecturesTopicsFilterInput
          $limit: Int
        ) {
          listLecturesTopics(filter: $filter, limit: $limit) {
            items {
              id
              topicId
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const existingTopics = existingResponse.data.listLecturesTopics.items || [];
    const existingTopicIds = existingTopics.map(t => t.topicId);

    // Determine topics to add and remove
    const topicsToAdd = newTopicIds.filter(id => !existingTopicIds.includes(id));
    const topicsToRemove = existingTopics.filter(t => !newTopicIds.includes(t.topicId));

    // Add new topics
    await Promise.all(
      topicsToAdd.map(topicId => createLectureTopicRelation(lectureId, topicId))
    );

    // Remove obsolete topics
    await Promise.all(
      topicsToRemove.map(topic =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesTopics(
              $input: DeleteLecturesTopicsInput!
              $condition: ModelLecturesTopicsConditionInput
            ) {
              deleteLecturesTopics(input: $input, condition: $condition) {
                id
              }
            }
          `,
          variables: {
            input: { id: topic.id }
          }
        })
      )
    );
  };

  const deleteAllLectureSpeakers = async (lectureId) => {
    const response = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesSpeakers(
          $filter: ModelLecturesSpeakersFilterInput
          $limit: Int
        ) {
          listLecturesSpeakers(filter: $filter, limit: $limit) {
            items {
              id
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const speakers = response.data.listLecturesSpeakers.items || [];
    
    await Promise.all(
      speakers.map(speaker =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesSpeakers(
              $input: DeleteLecturesSpeakersInput!
            ) {
              deleteLecturesSpeakers(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: { id: speaker.id }
          }
        })
      )
    );
  };

  const deleteAllLectureTopics = async (lectureId) => {
    const response = await client.graphql({
      query: /* GraphQL */ `
        query ListLecturesTopics(
          $filter: ModelLecturesTopicsFilterInput
          $limit: Int
        ) {
          listLecturesTopics(filter: $filter, limit: $limit) {
            items {
              id
            }
          }
        }
      `,
      variables: {
        filter: { lectureId: { eq: lectureId } },
        limit: 10000
      }
    });

    const topics = response.data.listLecturesTopics.items || [];
    
    await Promise.all(
      topics.map(topic =>
        client.graphql({
          query: /* GraphQL */ `
            mutation DeleteLecturesTopics(
              $input: DeleteLecturesTopicsInput!
            ) {
              deleteLecturesTopics(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: { id: topic.id }
          }
        })
      )
    );
  };

  return {
    fetchLecture,
    createLecture,
    updateLecture,
    deleteLecture,
    isLoading,
    error
  };
};